import type { FC, ReactNode } from 'react';
import classnames from 'classnames';
import { Translation } from 'react-i18next';
import type { CloseButtonProps } from 'react-toastify';
import { toast } from 'react-toastify';

import plusIcon from 'app/icons/plus.svg';
import checkIcon from 'app/icons/check.svg';
import infoIcon from 'app/icons/info_empty.svg';
import warningIcon from 'app/icons/warning_empty.svg';
import Icon from 'app/components/Icon';
import Button from 'app/components/Button/ResetedButton';

import style from './style.scss';

interface NotificationData {
  type: 'success' | 'info' | 'warning' | 'error';
  message: ReactNode;
  actions?: ReactNode;
}

interface Props {
  message: NotificationData['message'];
  type: NotificationData['type'];
  actions?: ReactNode;
  className?: string;
  containerClassName?: string;
}

const typeClassNameMap = {
  success: style.notification_success,
  info: style.notification_info,
  warning: style.notification_warning,
  error: style.notification_error
};

const typeIconMap = {
  success: (
    <div className={style.successIconContainer}>
      <Icon href={checkIcon} className={`${style.notifIcon} ${style.notifIcon_success}`} />
    </div>
  ),
  info: <Icon href={infoIcon} className={`${style.notifIcon} ${style.notifIcon_info}`} />,
  warning: <Icon href={warningIcon} className={`${style.notifIcon} ${style.notifIcon_warning}`} />,
  error: <Icon href={warningIcon} className={`${style.notifIcon} ${style.notifIcon_warning}`} />
};

const Notification: FC<Props> = ({
  message,
  type,
  className,
  containerClassName,
  actions = null
}) => {
  const notificationClassName = classnames(style.notification, typeClassNameMap[type], className);

  return (
    <div className={containerClassName}>
      <div className={notificationClassName}>
        {typeIconMap[type]}
        <div className={style.mainNotifContainer}>
          <div>{message}</div>
          {actions}
        </div>
      </div>
    </div>
  );
};

Notification.displayName = 'Notification';

const NotificationCloseButton: FC<CloseButtonProps> = ({ closeToast }) => (
  <Button className={style.closeButton} onClick={closeToast}>
    <Icon href={plusIcon} className={style.closeIcon} />
  </Button>
);

const notifier = (data: NotificationData) => {
  toast(<Notification type={data.type} message={data.message} actions={data.actions} />, {
    closeButton: NotificationCloseButton
  });
};

function createSuccessNotification(
  messageKey: string,
  variables: { [key: string]: string } = {},
  actions: ReactNode = null
) {
  notifier({
    type: 'success',
    message: <Translation>{t => <div>{t(messageKey, variables)}</div>}</Translation>,
    actions
  });
}

function createInfoNotification(messageKey: string, variables: { [key: string]: string } = {}) {
  notifier({
    type: 'info',
    message: <Translation>{t => <div>{t(messageKey, variables)}</div>}</Translation>
  });
}

function createWarningNotification(messageKey: string, variables: { [key: string]: string } = {}) {
  notifier({
    type: 'warning',
    message: <Translation>{t => <div>{t(messageKey, variables)}</div>}</Translation>
  });
}

export default notifier;
export type { NotificationData };
export {
  createSuccessNotification,
  createInfoNotification,
  createWarningNotification,
  Notification
};
